import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { motion } from "framer-motion";
import { Box, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import Footer from "../components/landing/Footer";
import Navigation from "../components/landing/Navigation";
import ModelViewer from "../components/stage/ModelViewer";
import { api, BASE } from "../services/api";

function resolveUrl(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function ImageGallery({ images }) {
    const [idx, setIdx] = useState(0);
    if (!images?.length) return null;
    const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
    const next = () => setIdx(i => (i + 1) % images.length);
    return (
        <div className="space-y-2">
            <div className="relative rounded-xl overflow-hidden bg-nova-black/50 aspect-video">
                <img src={resolveUrl(images[idx])} alt="" className="w-full h-full object-cover" />
                {images.length > 1 && (
                    <>
                        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                            {images.map((_, i) => (
                                <button key={i} onClick={() => setIdx(i)}
                                    className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-nova-cyan" : "bg-white/30"}`} />
                            ))}
                        </div>
                    </>
                )}
            </div>
            {images.length > 1 && (
                <div className="grid grid-cols-5 gap-1.5">
                    {images.map((url, i) => (
                        <button key={i} onClick={() => setIdx(i)}
                            className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${i === idx ? "border-nova-cyan/60" : "border-transparent opacity-50 hover:opacity-80"}`}>
                            <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function VizualizaciePage() {
    const [models, setModels]   = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getModels3d()
            .then(setModels)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-nova-black text-white">
            <SEO
                title="3D Vizualizácie"
                description="Interaktívne 3D vizualizácie zariadení a riešení KB MUSIC. Preskúmajte pódiové a AV inštalácie v 3D priamo v prehliadači."
                keywords="3D vizualizácia, pódium 3D, stage design, AV riešenia, interaktívny 3D model, pódiová technika"
                canonical="/vizualizacie"
            />

            <Navigation />

            <main className="max-w-7xl mx-auto px-6 sm:px-12 pt-36 pb-16">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-16"
                >
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-4">/ 3D Vizualizácie</div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-12">
                        Váš event v 3D <span className="text-nova-cyan">ešte pred štartom</span>
                    </h1>

                    {/* Feature block */}
                    <div className="relative rounded-2xl overflow-hidden border border-white/8">
                        {/* Background glow */}
                        <div className="absolute inset-0 bg-gradient-to-br from-nova-cyan/5 via-transparent to-transparent pointer-events-none" />
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-nova-cyan/8 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative grid lg:grid-cols-2">
                            {/* Left — text */}
                            <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center gap-6">
                                <div className="space-y-4">
                                    <p className="text-zinc-200 text-base sm:text-lg leading-relaxed">
                                        Disponujeme softvérom <span className="text-white font-semibold">Capture</span> v najvyššej verzii — profesionálnym nástrojom pre 3D vizualizáciu eventov, ktorý používajú popredné produkčné spoločnosti po celom svete.
                                    </p>
                                    <p className="text-zinc-500 text-sm leading-relaxed">
                                        Vytvoríme presný 3D model vášho priestoru so svetelným, zvukovým aj video riešením ešte pred začiatkom realizácie. Zákazník vidí výsledok vopred — bez prekvapení na deň D.
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {["Svetelný dizajn", "LED & Video", "Zvuk", "Pódium & Truss"].map(tag => (
                                        <span key={tag} className="px-3 py-1.5 rounded-full text-xs font-mono border border-white/10 text-zinc-400 bg-white/4">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Right — Capture card */}
                            <div className="border-t lg:border-t-0 lg:border-l border-white/8 p-8 sm:p-10 lg:p-12 flex flex-col gap-8 bg-nova-surface/20">
                                {/* Logo + version */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-xl bg-white/6 border border-white/10 flex items-center justify-center p-2.5">
                                            <img src="/logo-capture.png" alt="Capture" className="w-full h-full object-contain" style={{ filter: "brightness(0) invert(1)" }} />
                                        </div>
                                        <div>
                                            <div className="font-heading font-black text-white text-lg leading-none">Capture</div>
                                            <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">Visualization Software</div>
                                        </div>
                                    </div>
                                    <span className="px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold uppercase tracking-wider bg-nova-cyan/15 text-nova-cyan border border-nova-cyan/25">
                                        Ultimate
                                    </span>
                                </div>

                                {/* Specs */}
                                <div className="space-y-0 divide-y divide-white/6">
                                    {[
                                        { label: "Verzia",   value: "Capture Ultimate" },
                                        { label: "Výstup",   value: "3D render + interaktívny model" },
                                        { label: "Termín",   value: "Vizualizácia do 48 hodín" },
                                        { label: "Eventy",   value: "Svadby · Festivaly · Corporate" },
                                    ].map(({ label, value }) => (
                                        <div key={label} className="flex items-center justify-between py-3">
                                            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">{label}</span>
                                            <span className="text-white text-sm font-medium">{value}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA chip */}
                                <Link to="/#contact"
                                    className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-nova-cyan text-black text-sm font-semibold hover:bg-nova-cyan/90 transition-all hover:shadow-[0_0_24px_rgba(0,153,255,0.3)]">
                                    Objednať vizualizáciu
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Models */}
                {loading ? (
                    <div className="flex items-center justify-center py-32">
                        <div className="font-mono text-sm text-zinc-600 animate-pulse">Načítavam…</div>
                    </div>
                ) : models.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center justify-center py-32 gap-4 text-center"
                    >
                        <Box className="w-10 h-10 text-zinc-800" />
                        <div className="font-heading font-bold text-white text-lg">Zatiaľ žiadne modely</div>
                        <div className="text-zinc-600 text-sm max-w-xs">3D vizualizácie budú čoskoro k dispozícii.</div>
                    </motion.div>
                ) : (
                    <div className="space-y-8 mb-20">
                        {models.map((m, idx) => {
                            const ext      = m.file_url?.split(".").pop().toUpperCase() || "3D";
                            const flip     = idx % 2 === 1;
                            const hasBody  = m.body && m.body !== "<p></p>" && m.body.trim() !== "";
                            const hasImgs  = m.images?.length > 0;
                            return (
                                <motion.div
                                    key={m.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                                    className="rounded-2xl border border-white/8 bg-nova-surface/20 overflow-hidden"
                                >
                                    {/* Top: 3D viewer + info */}
                                    <div className={`flex flex-col ${flip ? "lg:flex-row-reverse" : "lg:flex-row"}`}>
                                        <div className="lg:w-3/5 h-72 sm:h-96 lg:h-[420px] shrink-0">
                                            <ModelViewer fileUrl={m.file_url} height="100%" showControls />
                                        </div>
                                        <div className={`lg:w-2/5 flex flex-col justify-center gap-4 p-7 lg:p-9 border-t lg:border-t-0 ${flip ? "lg:border-r" : "lg:border-l"} border-white/8`}>
                                            <div className="flex items-center gap-2.5 flex-wrap">
                                                <span className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-nova-cyan/15 text-nova-cyan border border-nova-cyan/25">{ext}</span>
                                                {m.tags?.slice(0, 3).map(t => (
                                                    <span key={t} className="font-mono text-[9px] px-2 py-0.5 rounded-sm bg-white/5 text-zinc-500 border border-white/8">{t}</span>
                                                ))}
                                            </div>
                                            <div>
                                                <h2 className="font-heading font-black text-white text-xl sm:text-2xl tracking-tight mb-2">{m.title}</h2>
                                                {m.description && (
                                                    <p className="text-zinc-400 text-sm leading-relaxed">{m.description}</p>
                                                )}
                                            </div>
                                            <div className="font-mono text-[9px] text-zinc-700 uppercase tracking-wider pb-2 border-b border-white/5">
                                                Drag → otočiť · Scroll → zoom · Pravé tl. → posunúť
                                            </div>
                                            <Link
                                                to={`/vizualizacie/${m.id}`}
                                                className="inline-flex items-center gap-2 text-sm font-medium text-zinc-400 hover:text-nova-cyan transition-colors group/link"
                                            >
                                                Zobraziť detail
                                                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-0.5 transition-transform" />
                                            </Link>
                                        </div>
                                    </div>

                                    {/* Bottom: images + rich text (if any) */}
                                    {(hasImgs || hasBody) && (
                                        <div className="border-t border-white/8 p-7 lg:p-9">
                                            <div className={`flex flex-col gap-8 ${hasImgs && hasBody ? "lg:flex-row" : ""}`}>
                                                {hasImgs && (
                                                    <div className={hasBody ? "lg:w-2/5 shrink-0" : "w-full max-w-lg"}>
                                                        <ImageGallery images={m.images} />
                                                    </div>
                                                )}
                                                {hasBody && (
                                                    <div className="flex-1 min-w-0">
                                                        <div
                                                            className="prose prose-invert prose-sm max-w-none
                                                                prose-headings:font-heading prose-headings:tracking-tight
                                                                prose-h2:text-lg prose-h3:text-base
                                                                prose-p:text-zinc-400 prose-p:leading-relaxed
                                                                prose-li:text-zinc-400
                                                                prose-a:text-nova-cyan prose-a:no-underline hover:prose-a:underline
                                                                prose-strong:text-white"
                                                            dangerouslySetInnerHTML={{ __html: m.body }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="border border-white/8 rounded-xl p-8 sm:p-12 bg-nova-surface/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
                >
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-3">/ Záujem o konkrétny návrh?</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-2">
                            Pripravíme 3D návrh presne pre vaše miesto
                        </h2>
                        <p className="text-zinc-400 text-sm max-w-lg">
                            Pošlite nám pôdorys miesta a my navrhneme optimálne osvetlenie, zvuk a LED setup šitý na mieru.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                        <Link to="/#contact"
                            className="inline-flex items-center gap-2 h-11 px-6 bg-nova-cyan text-black font-semibold text-sm rounded-md hover:saturate-125 transition-all hover:shadow-[0_0_24px_rgba(0,153,255,0.38)]">
                            Kontaktovať KB MUSIC
                        </Link>
                        <Link to="/technika"
                            className="inline-flex items-center justify-center h-11 px-6 border border-white/15 text-white text-sm font-medium rounded-md hover:border-white/30 hover:bg-white/5 transition-all">
                            Zobraziť techniku
                        </Link>
                    </div>
                </motion.div>
            </main>

            <Footer />
        </div>
    );
}
